import { useState } from "react";
import { Phone, Mail, MapPin, Send, Clock, CheckCircle2 } from "lucide-react";
import { companyInfo, services } from "../mock";
import { toast } from "../hooks/use-toast";

export default function Contatti() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    servizio: "",
    messaggio: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.messaggio) {
      toast({ title: "Campi mancanti", description: "Compila almeno nome, email e messaggio." });
      return;
    }
    setLoading(true);
    // Save locally to simulate submission
    setTimeout(() => {
      const existing = JSON.parse(localStorage.getItem("rota_requests") || "[]");
      existing.push({ ...form, createdAt: new Date().toISOString() });
      localStorage.setItem("rota_requests", JSON.stringify(existing));
      setLoading(false);
      setSubmitted(true);
      toast({ title: "Richiesta inviata", description: "Ti ricontatteremo entro 24 ore lavorative." });
      setForm({ nome: "", email: "", telefono: "", servizio: "", messaggio: "" });
    }, 800);
  };

  return (
    <div>
      {/* HERO */}
      <section className="pt-40 pb-16 bg-sage-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Contatti</span>
            <h1 className="font-serif text-5xl md:text-7xl text-sage-900 mt-4 leading-[1.05] mb-6">
              Parliamo del tuo<br />
              <span className="italic text-sage-700">giardino</span>.
            </h1>
            <p className="text-lg text-sage-800/70 leading-relaxed">
              Preventivo e sopralluogo sempre gratuiti. Scrivici o chiamaci: ti ricontatteremo al più presto.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Info */}
            <div className="lg:col-span-2 space-y-6 reveal">
              <div className="p-8 bg-sage-50 rounded">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                  <Phone className="w-5 h-5 text-sage-700" strokeWidth={1.5} />
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-sage-600">Telefono</div>
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`} className="font-serif text-2xl text-sage-900 mt-2 block hover:text-sage-700">
                  {companyInfo.phone}
                </a>
              </div>

              <div className="p-8 bg-sage-50 rounded">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                  <Mail className="w-5 h-5 text-sage-700" strokeWidth={1.5} />
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-sage-600">Email</div>
                <a href={`mailto:${companyInfo.email}`} className="font-serif text-lg text-sage-900 mt-2 block hover:text-sage-700 break-all">
                  {companyInfo.email}
                </a>
              </div>

              <div className="p-8 bg-sage-50 rounded">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                  <MapPin className="w-5 h-5 text-sage-700" strokeWidth={1.5} />
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-sage-600">Sede</div>
                <p className="font-serif text-lg text-sage-900 mt-2">{companyInfo.address}</p>
              </div>

              <div className="p-8 bg-sage-800 text-white rounded">
                <div className="w-12 h-12 rounded-full bg-sage-700 flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-sage-100" strokeWidth={1.5} />
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-sage-200">Orari</div>
                <div className="font-serif text-lg mt-2 space-y-1">
                  <div>Lun – Ven: 8:00 — 18:00</div>
                  <div>Sab: 8:00 — 13:00</div>
                  <div className="text-sage-300 text-sm">Domenica chiuso</div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3 reveal">
              <div className="bg-cream p-10 md:p-12 rounded">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto rounded-full bg-sage-100 flex items-center justify-center mb-6">
                      <CheckCircle2 className="w-10 h-10 text-sage-700" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-serif text-3xl text-sage-900 mb-4">Grazie!</h3>
                    <p className="text-sage-700 mb-8 max-w-md mx-auto">
                      La tua richiesta è stata inviata correttamente. Ti ricontatteremo entro 24 ore lavorative.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="inline-flex items-center gap-2 px-6 py-3 border border-sage-700 text-sage-800 rounded-full text-sm hover:bg-sage-700 hover:text-white transition-all"
                    >
                      Invia un'altra richiesta
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Modulo contatti</span>
                    <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mt-3 mb-8">Richiedi un preventivo</h2>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs tracking-[0.2em] uppercase text-sage-700 mb-2">Nome e cognome *</label>
                          <input
                            type="text"
                            name="nome"
                            value={form.nome}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded bg-white border border-sage-200 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500/20 transition-all text-sage-900"
                            placeholder="Mario Rossi"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs tracking-[0.2em] uppercase text-sage-700 mb-2">Telefono</label>
                          <input
                            type="tel"
                            name="telefono"
                            value={form.telefono}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded bg-white border border-sage-200 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500/20 transition-all text-sage-900"
                            placeholder="348 1234567"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs tracking-[0.2em] uppercase text-sage-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded bg-white border border-sage-200 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500/20 transition-all text-sage-900"
                          placeholder="mario.rossi@email.it"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs tracking-[0.2em] uppercase text-sage-700 mb-2">Servizio d'interesse</label>
                        <select
                          name="servizio"
                          value={form.servizio}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded bg-white border border-sage-200 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500/20 transition-all text-sage-900"
                        >
                          <option value="">Seleziona un servizio</option>
                          {services.map((s) => (
                            <option key={s.id} value={s.title}>{s.title}</option>
                          ))}
                          <option value="altro">Altro</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs tracking-[0.2em] uppercase text-sage-700 mb-2">Messaggio *</label>
                        <textarea
                          name="messaggio"
                          value={form.messaggio}
                          onChange={handleChange}
                          rows={5}
                          className="w-full px-4 py-3 rounded bg-white border border-sage-200 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500/20 transition-all text-sage-900 resize-none"
                          placeholder="Raccontaci il tuo progetto..."
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="group inline-flex items-center gap-3 px-8 py-4 bg-sage-700 text-white rounded-full text-sm tracking-wide hover:bg-sage-800 transition-all disabled:opacity-60"
                      >
                        {loading ? "Invio in corso..." : "Invia richiesta"}
                        <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </button>

                      <p className="text-xs text-sage-600 pt-2">
                        * Campi obbligatori. I dati inseriti sono usati solo per rispondere alla tua richiesta.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="rounded overflow-hidden shadow-lg reveal">
            <iframe
              title="Mappa Rota Giardinaggio"
              src="https://www.google.com/maps?q=Locatello%20BG%20via%20Ca%20Felisa%204&output=embed"
              className="w-full h-[450px] border-0"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
